/**
 * Движок опасностей (City Resilience Hack, 24.09.2026): точечная опасность
 * (пожар, дым, затопление, угроза, закрытый проход) и горящие помещения
 * тревоги → блоки маршрутного графа. Главное требование заказчиков — ЛЮБОЙ
 * маршрут (обычный и аварийный) обходит опасность; поэтому всё здесь —
 * чистые функции, и обход проверяется тестом, а не пожаром.
 *
 * Два потребителя одних и тех же блоков:
 *  - аварийный маршрут (planEscape) — блоки идут в EmergencyContext как есть,
 *    дым — штрафом;
 *  - обычный маршрут (planRoute) блокировок не знает — ему отдаётся граф без
 *    перекрытого (filterGraph). Дым обычный маршрут не запрещает: это штраф,
 *    а не стена, и planRoute штрафов не умеет.
 *
 * Геометрия — локальная равнопромежуточная проекция вокруг центра опасности:
 * на радиусах до 100 м её погрешность — сантиметры.
 */
import type { LonLat } from '@app/geo'
import { type Adjacent, type BuiltGraph, edgeKey, type Venue } from '@app/rvf'
import type { PointHazard, PointHazardKind, RouteDetour } from './alert-types'

/** Почему узел/ребро перекрыто — для строки «в обход …». */
export interface BlockCause {
  readonly kind: PointHazardKind | 'units'
  readonly levelId: string | null
}

export interface HazardBlocks {
  /** Узлы, куда маршрут не заходит. */
  readonly blockedNodes: ReadonlySet<string>
  /** Рёбра (ключ edgeKey), по которым маршрут не идёт. */
  readonly blockedEdges: ReadonlySet<string>
  /** Узлы в дыму — проходимы со штрафом (только аварийный маршрут). */
  readonly smokeNodes: ReadonlySet<string>
  /** Причина по ключу `n:{узел}` / `e:{ребро}`; первая записанная побеждает. */
  readonly causes: ReadonlyMap<string, BlockCause>
}

export const EMPTY_BLOCKS: HazardBlocks = {
  blockedNodes: new Set(),
  blockedEdges: new Set(),
  smokeNodes: new Set(),
  causes: new Map(),
}

/** Кольцо дыма вокруг пожара, м (сверх радиуса самого пожара). */
export const FIRE_SMOKE_RING_M = 15
/** Задымление вокруг горящего помещения, м. */
const UNIT_SMOKE_NEAR_M = 25

const M_PER_DEG_LAT = 110_540
const M_PER_DEG_LON_EQ = 111_320

/** Точка в метрах относительно центра (x — восток, y — север). */
function toLocal(center: LonLat, p: LonLat): readonly [number, number] {
  const cos = Math.cos(center[1] * (Math.PI / 180))
  return [(p[0] - center[0]) * M_PER_DEG_LON_EQ * cos, (p[1] - center[1]) * M_PER_DEG_LAT]
}

/** Расстояние от центра до отрезка a–b, м. */
function segmentDistM(center: LonLat, a: LonLat, b: LonLat): number {
  const [ax, ay] = toLocal(center, a)
  const [bx, by] = toLocal(center, b)
  const dx = bx - ax
  const dy = by - ay
  const len2 = dx * dx + dy * dy
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, -(ax * dx + ay * dy) / len2))
  return Math.hypot(ax + t * dx, ay + t * dy)
}

function distM(center: LonLat, p: LonLat): number {
  const [x, y] = toLocal(center, p)
  return Math.hypot(x, y)
}

/** Опасность действует на этаже: null у опасности — на всех этажах. */
function onLevel(h: PointHazard, levelId: string | null): boolean {
  return h.level === null || levelId === null || h.level === levelId
}

/** Перекрывающие виды; дым — только штраф. */
function blocks(kind: PointHazardKind): boolean {
  return kind !== 'smoke'
}

/**
 * Опасность в точке: первая, в круг которой попадает точка на этом этаже.
 * Перекрывающие важнее дыма — человеку в огне говорим про огонь.
 */
export function hazardAt(
  point: LonLat,
  levelId: string | null,
  hazards: readonly PointHazard[],
): PointHazard | null {
  let smoke: PointHazard | null = null
  for (const h of hazards) {
    if (!onLevel(h, levelId) || distM(h.point, point) > h.radius_m) continue
    if (blocks(h.kind)) return h
    smoke ??= h
  }
  return smoke
}

/**
 * Прямой отрезок (пунктир «вне сети» к двери) задевает перекрывающую опасность
 * на этаже `levelId` — такой пунктир вёл бы человека сквозь огонь.
 */
export function segmentHitsHazard(
  a: LonLat,
  b: LonLat,
  levelId: string | null,
  hazards: readonly PointHazard[],
): PointHazard | null {
  for (const h of hazards) {
    if (blocks(h.kind) && onLevel(h, levelId) && segmentDistM(h.point, a, b) <= h.radius_m) {
      return h
    }
  }
  return null
}

class BlocksBuilder {
  readonly nodes = new Set<string>()
  readonly edges = new Set<string>()
  readonly smoke = new Set<string>()
  readonly causes = new Map<string, BlockCause>()

  node(id: string, cause: BlockCause): void {
    this.nodes.add(id)
    if (!this.causes.has(`n:${id}`)) this.causes.set(`n:${id}`, cause)
  }
  edge(key: string, cause: BlockCause): void {
    this.edges.add(key)
    if (!this.causes.has(`e:${key}`)) this.causes.set(`e:${key}`, cause)
  }
  smokeNode(id: string, cause: BlockCause): void {
    this.smoke.add(id)
    if (!this.causes.has(`n:${id}`)) this.causes.set(`n:${id}`, cause)
  }
  build(): HazardBlocks {
    // Перекрытый узел не бывает одновременно задымлённым.
    for (const id of this.nodes) this.smoke.delete(id)
    if (this.nodes.size + this.edges.size + this.smoke.size === 0) return EMPTY_BLOCKS
    return {
      blockedNodes: this.nodes,
      blockedEdges: this.edges,
      smokeNodes: this.smoke,
      causes: this.causes,
    }
  }
}

/**
 * Точечные опасности → блоки графа.
 *
 * Перекрыто (fire/flood/danger/closed): узлы внутри круга на этаже опасности
 * и рёбра, чей отрезок задевает круг. Межэтажное ребро (лестница, лифт)
 * перекрыто, если его конец на этаже опасности внутри круга: подниматься в
 * огонь нельзя, даже если соседний этаж чист. Дым (smoke) — штраф узлам
 * внутри круга; пожар дополнительно даёт кольцо дыма шириной FIRE_SMOKE_RING_M.
 */
export function pointHazardBlocks(
  graph: BuiltGraph,
  hazards: readonly PointHazard[],
): HazardBlocks {
  if (hazards.length === 0) return EMPTY_BLOCKS
  const out = new BlocksBuilder()
  for (const h of hazards) collectPointHazard(graph, h, out)
  return out.build()
}

function collectPointHazard(graph: BuiltGraph, h: PointHazard, out: BlocksBuilder): void {
  const cause: BlockCause = { kind: h.kind, levelId: h.level }
  const smokeCause: BlockCause = { kind: 'smoke', levelId: h.level }
  const hard = blocks(h.kind)
  const smokeR = h.kind === 'fire' ? h.radius_m + FIRE_SMOKE_RING_M : h.radius_m
  for (const [id, node] of graph.nodes) {
    if (!onLevel(h, node.level)) continue
    const d = distM(h.point, node.point)
    if (hard && d <= h.radius_m) out.node(id, cause)
    else if (d <= smokeR) out.smokeNode(id, h.kind === 'smoke' ? cause : smokeCause)
  }
  if (hard) collectHitEdges(graph, h, cause, out)
}

/** Рёбра, которые круг опасности перерезает; каждое ребро — один раз. */
function collectHitEdges(
  g: BuiltGraph,
  h: PointHazard,
  cause: BlockCause,
  out: BlocksBuilder,
): void {
  const seen = new Set<string>()
  for (const [from, list] of g.adj) {
    const a = g.nodes.get(from)
    for (const entry of a ? list : []) {
      const key = edgeKey(from, entry.to)
      const b = g.nodes.get(entry.to)
      if (!b || seen.has(key)) continue
      seen.add(key)
      if (edgeHit(h, a as GraphNodeLike, b)) out.edge(key, cause)
    }
  }
}

type GraphNodeLike = { readonly level: string; readonly point: LonLat }

function edgeHit(h: PointHazard, a: GraphNodeLike, b: GraphNodeLike): boolean {
  if (a.level === b.level) {
    return onLevel(h, a.level) && segmentDistM(h.point, a.point, b.point) <= h.radius_m
  }
  // Межэтажное ребро: в плане это почти точка — смотрим концы на этаже опасности.
  const endIn = (n: GraphNodeLike) => onLevel(h, n.level) && distM(h.point, n.point) <= h.radius_m
  return endIn(a) || endIn(b)
}

// ── горящие помещения тревоги площадки ──

function pointInRing(pt: LonLat, ring: readonly (readonly number[])[]): boolean {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const ax = ring[i]?.[0] as number
    const ay = ring[i]?.[1] as number
    const bx = ring[j]?.[0] as number
    const by = ring[j]?.[1] as number
    if (ay > pt[1] !== by > pt[1] && pt[0] < ((bx - ax) * (pt[1] - ay)) / (by - ay) + ax) {
      inside = !inside
    }
  }
  return inside
}

/** Минимальное расстояние до вершин контура (для порога дыма хватает вершин). */
function nearRingM(pt: LonLat, ring: readonly (readonly number[])[]): number {
  let near = Number.POSITIVE_INFINITY
  for (const rp of ring) {
    near = Math.min(near, distM(pt, [rp[0] as number, rp[1] as number]))
    if (near <= UNIT_SMOKE_NEAR_M) break
  }
  return near
}

/**
 * Горящие помещения → блоки. Перекрыто: узлы ВНУТРИ контура и его двери
 * (entrances) — маршрут туда не заходит вовсе. Задымлено: узлы того же этажа
 * ближе UNIT_SMOKE_NEAR_M к контуру — проходимы, но со штрафом: если другого
 * пути нет, лучше провести рядом с огнём, чем не вывести человека совсем.
 */
export function unitHazardBlocks(venue: Venue, unitIds: readonly string[]): HazardBlocks {
  if (unitIds.length === 0) return EMPTY_BLOCKS
  const out = new BlocksBuilder()
  for (const unitId of unitIds) {
    const unit = venue.units.find((u) => u.id === unitId)
    const ring = unit?.geometry.coordinates[0]
    if (unit && ring) collectUnit(venue, unit, ring, out)
  }
  return out.build()
}

function collectUnit(
  venue: Venue,
  unit: Venue['units'][number],
  ring: readonly (readonly number[])[],
  out: BlocksBuilder,
): void {
  const cause: BlockCause = { kind: 'units', levelId: unit.level }
  for (const e of unit.entrances ?? []) out.node(e, cause)
  for (const node of venue.network.nodes) {
    if (node.level !== unit.level) continue
    if (pointInRing(node.point, ring)) out.node(node.id, cause)
    else if (nearRingM(node.point, ring) <= UNIT_SMOKE_NEAR_M) out.smokeNode(node.id, cause)
  }
}

/**
 * Совместимый вид для аварийного кадра (live.ts, тесты v1): списки id.
 * Прежний `hazardToBlocks` из live.ts — теперь здесь.
 */
export function hazardToBlocks(
  venue: Venue,
  hazardUnitIds: readonly string[],
): { blocksNodes: string[]; smokeNodes: string[] } {
  const b = unitHazardBlocks(venue, hazardUnitIds)
  return { blocksNodes: [...b.blockedNodes], smokeNodes: [...b.smokeNodes] }
}

/** Объединение блоков; причина — от первого источника, где она есть. */
export function mergeBlocks(...parts: readonly HazardBlocks[]): HazardBlocks {
  const real = parts.filter((p) => p !== EMPTY_BLOCKS)
  if (real.length === 0) return EMPTY_BLOCKS
  if (real.length === 1) return real[0] as HazardBlocks
  const out = new BlocksBuilder()
  for (const p of real) {
    for (const id of p.blockedNodes) out.node(id, causeOf(p, `n:${id}`))
    for (const key of p.blockedEdges) out.edge(key, causeOf(p, `e:${key}`))
    for (const id of p.smokeNodes) out.smokeNode(id, causeOf(p, `n:${id}`))
  }
  return out.build()
}

const UNKNOWN_CAUSE: BlockCause = { kind: 'danger', levelId: null }

function causeOf(b: HazardBlocks, key: string): BlockCause {
  return b.causes.get(key) ?? UNKNOWN_CAUSE
}

/** Все блоки площадки: точечные опасности + горящие помещения тревоги. */
export function venueHazardBlocks(
  venue: Venue,
  graph: BuiltGraph,
  hazards: readonly PointHazard[],
  unitIds: readonly string[],
): HazardBlocks {
  return mergeBlocks(pointHazardBlocks(graph, hazards), unitHazardBlocks(venue, unitIds))
}

export function hasBlocks(b: HazardBlocks): boolean {
  return b.blockedNodes.size > 0 || b.blockedEdges.size > 0
}

const filtered = new WeakMap<BuiltGraph, WeakMap<HazardBlocks, BuiltGraph>>()

/**
 * Граф без перекрытого — для обычного planRoute, который блокировок не знает.
 * Узлы из `keep` (зона, где человек уже стоит) остаются вместе с рёбрами к
 * ним. Без блоков возвращается ТОТ ЖЕ объект графа; без keep результат
 * мемоизуется по паре (граф, блоки).
 */
export function filterGraph(
  graph: BuiltGraph,
  b: HazardBlocks,
  keep: ReadonlySet<string> = new Set(),
): BuiltGraph {
  if (!hasBlocks(b)) return graph
  const memo = keep.size === 0 ? filtered.get(graph)?.get(b) : undefined
  if (memo) return memo
  const alive = (id: string) => !b.blockedNodes.has(id) || keep.has(id)
  const nodes = new Map([...graph.nodes].filter(([id]) => alive(id)))
  const adj = new Map<string, readonly Adjacent[]>()
  for (const [from, list] of graph.adj) {
    if (!alive(from)) continue
    adj.set(
      from,
      list.filter(
        (e) =>
          alive(e.to) &&
          (keep.has(from) || keep.has(e.to) || !b.blockedEdges.has(edgeKey(from, e.to))),
      ),
    )
  }
  const out: BuiltGraph = graph.prefer ? { nodes, adj, prefer: graph.prefer } : { nodes, adj }
  if (keep.size === 0) {
    const perGraph = filtered.get(graph) ?? new WeakMap<HazardBlocks, BuiltGraph>()
    perGraph.set(b, out)
    filtered.set(graph, perGraph)
  }
  return out
}

/**
 * Зона, где человек уже стоит: связная область перекрытых узлов вокруг
 * стартового. Её нельзя запирать — иначе «вы в огне» превращается в «выхода
 * нет»; из неё выводим кратчайшим путём (аварийный маршрут — со штрафом дыма).
 * Пусто — старт не в перекрытом.
 */
export function zoneAround(graph: BuiltGraph, b: HazardBlocks, startId: string): Set<string> {
  const zone = new Set<string>()
  if (!b.blockedNodes.has(startId)) return zone
  const queue = [startId]
  zone.add(startId)
  while (queue.length > 0) {
    const id = queue.pop() as string
    for (const e of graph.adj.get(id) ?? []) {
      if (b.blockedNodes.has(e.to) && !zone.has(e.to)) {
        zone.add(e.to)
        queue.push(e.to)
      }
    }
  }
  return zone
}

/** Первая причина блока на пути (узел или ребро); null — путь чист. */
export function firstCause(
  nodeIds: readonly string[],
  b: HazardBlocks,
  withSmoke = false,
): BlockCause | null {
  for (let i = 0; i < nodeIds.length; i++) {
    const id = nodeIds[i] as string
    if (b.blockedNodes.has(id) || (withSmoke && b.smokeNodes.has(id))) {
      return causeOf(b, `n:${id}`)
    }
    const next = nodeIds[i + 1]
    if (next !== undefined && b.blockedEdges.has(edgeKey(id, next))) {
      return causeOf(b, `e:${edgeKey(id, next)}`)
    }
  }
  return null
}

/**
 * Добавка обхода к кратчайшему пути, м — округлена до 5 м: точнее человеку не
 * нужно и не честно (длины считаются по полилинии, а не по шагам).
 */
export function detourExtraM(shortestM: number, safeM: number): number {
  return Math.max(0, Math.round((safeM - shortestM) / 5) * 5)
}

/**
 * Обход: кратчайший путь по полному графу задевает блок, а безопасный — нет.
 * `withSmoke` — аварийный маршрут, для которого дым тоже причина обхода.
 * null — кратчайший путь чист (или его нет вовсе).
 */
export function detour(
  shortestNodeIds: readonly string[],
  shortestM: number,
  safeM: number,
  b: HazardBlocks,
  withSmoke = false,
): RouteDetour | null {
  // Перекрытие — главная причина, даже если дым на пути раньше (часто прямо у
  // старта: «обход из-за дыма» при горящем коридоре впереди — неправда).
  const cause =
    firstCause(shortestNodeIds, b) ?? (withSmoke ? firstCause(shortestNodeIds, b, true) : null)
  if (!cause) return null
  return { kind: cause.kind, levelId: cause.levelId, extraM: detourExtraM(shortestM, safeM) }
}
